import { useCallback, useEffect, useRef, useState } from 'react';
import { CustomerBadges, TripNeeds } from '../../components/TripExtras';
import { useHistory } from 'react-router-dom';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Haptics, NotificationType } from '@capacitor/haptics';
import { Briefcase, CalendarClock, Users, X, Zap } from 'lucide-react';
import { ANDROID_CHANNEL_NEW_RIDE_REQUESTS, formatMoney } from '@tcrd/shared';
import { Avatar, Button } from '@tcrd/ui';
import { api } from '../../lib/api';
import { env } from '../../lib/env';
import { fmtDateTime, fmtDuration, fmtKm } from '../../lib/format';
import type { RideRequestDto } from '../../lib/types';
import { useAuth } from '../../stores/auth';
import { playChime } from '../../lib/sound';

/** Evento interno: lo emiten el socket (app abierta) y el push en primer plano (Android). */
export const NEW_REQUEST_EVENT = 'tcrd:new-ride-request';
export const announceNewRequest = (requestId: string, opts: { silent?: boolean } = {}) =>
  window.dispatchEvent(new CustomEvent(NEW_REQUEST_EVENT, { detail: { requestId, silent: !!opts.silent } }));

const AUTO_CLOSE_MS = 60_000;

/** Android: notificación local en el canal de solicitudes → sonido por defecto del teléfono y vibración. */
async function ring(r: RideRequestDto) {
  if (!env.isNative) {
    playChime();
    return;
  }
  void Haptics.notification({ type: NotificationType.Warning }).catch(() => undefined);
  await LocalNotifications.schedule({
    notifications: [
      {
        id: Math.floor(Math.random() * 2_000_000_000),
        title: 'Nueva solicitud de viaje',
        body: `${r.service.name}: ${r.origin.address} → ${r.destination.address}`,
        channelId: ANDROID_CHANNEL_NEW_RIDE_REQUESTS,
        extra: { url: `/driver/requests/${r.id}` },
      },
    ],
  }).catch(() => undefined);
}

/**
 * Aviso a pantalla completa (estilo Uber) cuando entra una solicitud y la app está abierta.
 * Con la app cerrada o en segundo plano, el aviso es la notificación push del sistema.
 */
export function NewRequestAlert() {
  const history = useHistory();
  const isDriver = useAuth((s) => s.status === 'authenticated' && !!s.user && (s.user.roles.includes('DRIVER') || s.user.roles.includes('TENANT_ADMIN')));
  const [req, setReq] = useState<RideRequestDto | null>(null);
  const seen = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const close = useCallback(() => {
    setReq(null);
    if (timer.current) clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    if (!isDriver) return;
    const onNew = async (e: Event) => {
      const { requestId: id, silent } = (e as CustomEvent<{ requestId?: string; silent?: boolean }>).detail ?? {};
      if (!id || seen.current.has(id)) return; // el socket y el push llegan ambos: solo una vez
      seen.current.add(id);
      try {
        const r = await api<RideRequestDto>(`/ride-requests/${id}`);
        if (!['REQUESTED', 'INFO_REQUESTED'].includes(r.status)) return;
        setReq(r);
        if (!silent) void ring(r);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setReq(null), AUTO_CLOSE_MS);
      } catch {
        /* sin acceso o ya no existe */
      }
    };
    window.addEventListener(NEW_REQUEST_EVENT, onNew);
    const tap = env.isNative
      ? LocalNotifications.addListener('localNotificationActionPerformed', (a) => {
          const url = (a.notification.extra as { url?: string } | undefined)?.url;
          if (url?.startsWith('/driver/')) history.push(url);
        })
      : null;
    return () => {
      window.removeEventListener(NEW_REQUEST_EVENT, onNew);
      void tap?.then((h) => h.remove());
    };
  }, [isDriver, history]);

  if (!req) return null;
  const when = req.mode === 'NOW' ? 'Para ahora' : req.scheduledAt ? fmtDateTime(req.scheduledAt) : 'Recurrente';
  const open = () => {
    close();
    history.push(`/driver/requests/${req.id}`);
  };

  return (
    <div role="alertdialog" aria-modal="true" aria-label="Nueva solicitud de viaje" className="fixed inset-0 z-[2000] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] animate-[fadeIn_.15s_ease-out]">
      <div className="w-full max-w-md rounded-3xl bg-[var(--surface)] shadow-2xl overflow-hidden">
        <div className="bg-brand-red text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {req.mode === 'NOW' ? <Zap className="size-6" aria-hidden /> : <CalendarClock className="size-6" aria-hidden />}
            <div>
              <p className="text-xs uppercase tracking-wide opacity-90">Nueva solicitud</p>
              <p className="text-lg font-bold leading-tight">{req.service.name}</p>
            </div>
          </div>
          <button className="tap size-10 rounded-full bg-white/15 flex items-center justify-center" onClick={close} aria-label="Cerrar">
            <X className="size-5" />
          </button>
        </div>
        <div className="p-5 flex flex-col gap-4">
          <div className="flex items-center gap-3">
            {req.customer && <Avatar src={req.customer.photoUrl} firstName={req.customer.firstName} lastName={req.customer.lastName} size={44} />}
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{req.customer ? `${req.customer.firstName} ${req.customer.lastName}` : 'Cliente'}</p>
              {req.customer && <CustomerBadges c={req.customer} className="my-0.5" />}
              <p className="text-sm text-[var(--text-muted)]">{when}</p>
            </div>
            {(req.offeredFare || req.suggestedFare) && (
              <div className="text-right">
                <p className="text-xs text-[var(--text-muted)]">{req.offeredFare ? 'Ofrece' : 'Sugerido'}</p>
                <p className={`text-xl font-bold tabular-nums ${req.offeredFare ? 'text-green-700 dark:text-green-400' : ''}`}>{formatMoney(req.offeredFare ?? req.suggestedFare!.total)}</p>
              </div>
            )}
          </div>
          <ol className="flex flex-col gap-3 text-sm">
            <li className="flex gap-3">
              <span className="mt-1 size-3 shrink-0 rounded-full bg-brand-black dark:bg-white" aria-hidden />
              <span className="line-clamp-2">{req.origin.address}</span>
            </li>
            <li className="flex gap-3">
              <span className="mt-1 size-3 shrink-0 rounded-full bg-brand-red" aria-hidden />
              <span className="line-clamp-2">{req.destination.address}</span>
            </li>
          </ol>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[var(--text-muted)]">
            {req.route && (
              <span>
                <strong className="text-[var(--text)]">{fmtKm(req.route.distanceMeters)}</strong> · {fmtDuration(req.route.durationSeconds)}
              </span>
            )}
            <span className="flex items-center gap-1"><Users className="size-4" aria-hidden /> {req.passengers}</span>
            <span className="flex items-center gap-1"><Briefcase className="size-4" aria-hidden /> {req.luggage}</span>
          </div>
          <TripNeeds needs={req.needs} flightNumber={req.flightNumber} pkg={req.package} />
          {req.notes && <p className="text-sm rounded-xl bg-[var(--surface-2)] px-3 py-2 line-clamp-2">“{req.notes}”</p>}
          <div className="grid grid-cols-[1fr_2fr] gap-2 pt-1">
            <Button variant="secondary" size="lg" onClick={close}>Omitir</Button>
            <Button size="lg" onClick={open}>Ver y cotizar</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
